import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Treat 401 as "not logged in" silently — public pages mount AuthProvider and we don't want noisy console errors
      const { data, status } = await api.get("/auth/me", { validateStatus: (s) => s < 500 });
      if (status === 200 && data && data.user_id) {
        setUser(data);
        return data;
      }
      setUser(null);
      return null;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, AuthCallback will handle it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  async function login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }
  async function register(payload) {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  }
  async function logout() {
    await api.post("/auth/logout");
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

/**
 * Returns the correct landing route after login/register/Google.
 * - Admin     → /dashboard (admin overview)
 * - Therapist → /dashboard/therapy (session hub)
 * - No plan   → /choose-plan
 * - No wedding → /onboarding
 * - Else      → /dashboard
 */
export function routeAfterAuth(user) {
  if (!user) return "/login";
  if (user.role === "admin") return "/dashboard";
  if (user.role === "applicant") return "/careers/me";
  if (user.sales_rep && user.hired_from) return "/call-center";
  if (user.role === "therapist") return "/dashboard/therapy";
  if (user.role === "guest") return "/hub";
  if (user.account_type === "seller" && !user.plan_selected) return "/market/sell";
  if (user.is_customer && !user.plan_selected) return "/account/orders";
  if (!user.plan_selected) return "/choose-plan";
  if (!user.setup_fee_paid) return "/setup-fee";
  if (["vendor", "planner"].includes(user.role)) return "/dashboard";
  if (!user.has_wedding) return "/onboarding";
  return "/dashboard";
}
